import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { fDateSimple } from '../../../utils/formatTime';
import { Num, CABECALHO_TABELA_COMPACTO, BotaoExportar } from '../../../components/gps';
import Modal, { BotaoSecundario, Campo, classesEntrada } from '../../../components/gps/Modal';
import MenuAcoes from '../../../components/gps/MenuAcoes';

// Ainda em MUI - detalhe rapido do aparelho. Na fila para migrar.
import ModalDispositivoInfo from './components/modal-dispositivo-info';
import { linhasIniciais, OPCOES_LINHAS, rotuloLinhas } from '../../../components/gps/paginacao';
import TituloTela from '../../../components/gps/TituloTela';
import { resumoImeis } from '../../../utils/resumoImeis';

/* ============================================================================
   Busca de dispositivos - visual novo (shadcn + Tailwind).

   Reescrita no lugar. Contratos, os mesmos de antes:
     GET  /dispositivos/paginado-todos?page&rows&valor&campo&direcao&ordenar&status=-1&entidade=-1&regional=null
     POST /imei { lista }

   ESTA E A BUSCA GLOBAL: `status=-1`, `entidade=-1` e `regional=null` fixos, ou
   seja, procura em TODO o parque, incluindo inativos e apagados. E por isso que
   ela e separada da listagem de Dispositivos - e a tela de "esse aparelho existe
   em algum lugar?".

   TRES COISAS PRESERVADAS:

   1. SO BUSCA APOS CLICAR. A tela nao carrega nada ao abrir. Com busca global sem
      filtro de grupo, carregar tudo de entrada seria varrer o parque inteiro a
      cada visita.
   2. Os TRES campos de busca (Device ID, Nome, IMEI) - os mesmos.
   3. O REGISTRO DE IMEIs em lote, que aceita a lista separada por linha, virgula,
      ponto-e-virgula ou espaco.

   As 27 colunas da tabela antiga estao todas aqui, incluindo `deletedAt`
   ("Deletado"), que so existe nesta tela - e o que diferencia "nao encontrei" de
   "existe, mas foi apagado".
   ============================================================================ */

type Dispositivo = {
  deviceId?: string;
  nome?: string;
  deletedAt?: string | null;
  hardwareInfo_manufacturer?: string;
  hardwareInfo_model?: string;
  hardwareInfo_serialNumber?: string;
  softwareInfo_androidVersion?: string;
  networkInfo_imei?: string;
  networkInfo_wifiMacAddress?: string;
  networkInfo_telephonyInfos_phoneNumber_1?: string;
  networkInfo_telephonyInfos_phoneNumber_2?: string;
  networkInfo_telephonyInfos_carrierName_1?: string;
  networkInfo_telephonyInfos_carrierName_2?: string;
  memoryInfo_totalRam?: number | string;
  external_storage_detected?: string;
  cr?: string;
  cpf?: string;
  bateria?: number | string;
  wifi?: string;
  sinal_telefone?: string;
  sinal_dados_moveis?: string;
  consumo_wifi?: string;
  consumo_dados_moveis?: string;
  endereco?: string;
  versaoHub?: string;
  device_log_data?: string;
  grupoNome?: string;
  grupoCodigo?: number;
};


const TIPOS_BUSCA = [
  { label: 'Device ID', value: 'deviceId' },
  { label: 'Nome', value: 'nome' },
  { label: 'IMEI', value: 'networkInfo_imei' },
];

// As mesmas 26 colunas de dado da tabela antiga (a 27a era "Opcoes").
const COLUNAS: Array<{ campo: keyof Dispositivo; rotulo: string; largura: string; mono?: boolean }> = [
  { campo: 'device_log_data', rotulo: 'Última sincronização', largura: '150px', mono: true },
  { campo: 'nome', rotulo: 'Nome', largura: 'minmax(150px,1.4fr)' },
  { campo: 'deletedAt', rotulo: 'Deletado', largura: '110px', mono: true },
  { campo: 'hardwareInfo_manufacturer', rotulo: 'Marca', largura: '120px' },
  { campo: 'hardwareInfo_model', rotulo: 'Modelo', largura: '140px' },
  { campo: 'bateria', rotulo: 'Bateria', largura: '90px', mono: true },
  { campo: 'hardwareInfo_serialNumber', rotulo: 'Serial Number', largura: '150px', mono: true },
  { campo: 'softwareInfo_androidVersion', rotulo: 'OS', largura: '80px', mono: true },
  { campo: 'sinal_telefone', rotulo: 'Sinal/Telefone', largura: '120px' },
  { campo: 'sinal_dados_moveis', rotulo: 'Sinal/Dados', largura: '110px' },
  { campo: 'wifi', rotulo: 'WIFI', largura: '130px' },
  { campo: 'networkInfo_imei', rotulo: 'IMEI', largura: '150px', mono: true },
  { campo: 'networkInfo_wifiMacAddress', rotulo: 'MAC', largura: '140px', mono: true },
  { campo: 'cr', rotulo: 'CR', largura: '110px' },
  { campo: 'cpf', rotulo: 'CPF', largura: '130px', mono: true },
  { campo: 'endereco', rotulo: 'Endereço', largura: 'minmax(180px,1.6fr)' },
  { campo: 'consumo_wifi', rotulo: 'Consumo/wifi', largura: '120px', mono: true },
  { campo: 'consumo_dados_moveis', rotulo: 'Consumo/dados', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_phoneNumber_1', rotulo: 'Telefone 1', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_phoneNumber_2', rotulo: 'Telefone 2', largura: '130px', mono: true },
  { campo: 'networkInfo_telephonyInfos_carrierName_1', rotulo: 'Operadora 1', largura: '120px' },
  { campo: 'networkInfo_telephonyInfos_carrierName_2', rotulo: 'Operadora 2', largura: '120px' },
  { campo: 'memoryInfo_totalRam', rotulo: 'Memória RAM', largura: '130px', mono: true },
  { campo: 'external_storage_detected', rotulo: 'Armazenamento', largura: '130px' },
  { campo: 'versaoHub', rotulo: 'Versão do hub', largura: '120px', mono: true },
];

function texto(v: unknown) {
  const s = v === null || v === undefined || v === '' ? '' : String(v);
  return s || '—';
}

BuscaPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function BuscaPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [valor, setValor] = useState('');
  const [pesquisado, setPesquisado] = useState('');
  const [campo, setCampo] = useState('deviceId');

  const [linhas, setLinhas] = useState<Dispositivo[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  const [buscou, setBuscou] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  const [detalhe, setDetalhe] = useState<Dispositivo | null>(null);

  const [modalImei, setModalImei] = useState(false);
  const [imeis, setImeis] = useState('');
  const [enviandoImeis, setEnviandoImeis] = useState(false);

  const carregar = useCallback(async () => {
    if (!buscou || !pesquisado) return;
    setCarregando(true);
    setErro('');
    try {
      const r = await axios.get<any>(
        `/dispositivos/paginado-todos?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(pesquisado)}&campo=${campo}` +
          `&direcao=desc&ordenar=device_log_data&status=-1&entidade=-1&regional=null`
      );
      const lista = Array.isArray(r.data) ? r.data : r.data?.content || [];
      setLinhas(lista);
      setTotal(r.data?.totalElements ?? lista.length);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Ops ocorreu um erro ao pesquisar.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [buscou, pesquisado, campo, pagina, linhasPorPagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function buscar() {
    const termo = valor.trim();
    if (!termo) {
      enqueueSnackbar('Informe o que procurar.', { variant: 'warning' });
      return;
    }
    setPagina(0);
    setPesquisado(termo);
    setBuscou(true);
  }

  function limpar() {
    setValor('');
    setPesquisado('');
    setBuscou(false);
    setLinhas([]);
    setTotal(0);
    setPagina(0);
    setErro('');
  }

  // Aceita a lista separada por linha, virgula, ponto-e-virgula ou espaco -
  // mesma quebra da tela antiga.
  const listaImeis = useMemo(
    () =>
      imeis
        .split(/\r?\n|,|;|\s+/)
        .map((i) => i.trim())
        .filter(Boolean),
    [imeis]
  );

  async function registrarImeis() {
    if (!listaImeis.length) {
      enqueueSnackbar('Informe ao menos um IMEI para registrar.', { variant: 'warning' });
      return;
    }
    setEnviandoImeis(true);
    try {
      /* Era `POST /imei`, rota que nao existe (a da API e /dispositivos/imei):
         em producao dava 404 sempre; em dev o Vite devolvia o index.html com 200
         e a tela dizia "sucesso" sem registrar nada. */
      const { data } = await axios.post('/dispositivos/imei', { lista: listaImeis });
      enqueueSnackbar(resumoImeis(data, listaImeis.length), { variant: 'success' });
      setModalImei(false);
      setImeis('');
    } catch (e: any) {
      // A API explica o motivo (ex.: "Espere cerca de 1 minuto") - mostra-lo.
      enqueueSnackbar(e?.response?.data?.message || 'Ops ocorreu um erro ao registrar os IMEIs.', { variant: 'error' });
    } finally {
      setEnviandoImeis(false);
    }
  }

  const grade = `56px minmax(200px,1.8fr) ${COLUNAS.map((c) => c.largura).join(' ')}`;
  const totalPaginas = Math.max(1, Math.ceil(total / linhasPorPagina));

  return (
    <Page title="Busca de Dispositivos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
Procura em <strong>todo o parque</strong>, incluindo inativos e apagados — é a tela do "esse
              aparelho existe em algum lugar?". A coluna <em>Deletado</em> é o que diferencia "não encontrei"
              de "existe, mas foi apagado".
                </>
              }
            >
              Busca de dispositivos
            </TituloTela>
          </div>

          <button
            type="button"
            onClick={() => setModalImei(true)}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Registrar IMEIs
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            value={campo}
            onChange={(e) => setCampo(e.target.value)}
            className="h-9 rounded-lg border border-line bg-surface px-2 text-[12px] font-semibold text-muted-foreground outline-none focus:border-primary"
          >
            {TIPOS_BUSCA.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
            placeholder={TIPOS_BUSCA.find((t) => t.value === campo)?.label}
            className="h-9 w-full max-w-[320px] rounded-lg border border-line bg-surface px-3 text-[12.5px] outline-none placeholder:text-faint focus:border-primary"
          />

          <button
            type="button"
            onClick={buscar}
            className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            Pesquisar
          </button>

          <button
            type="button"
            onClick={limpar}
            className="h-9 rounded-lg border border-line px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
          >
            Limpar
          </button>

          {buscou && linhas.length > 0 && (
            /* O botao proprio desta tela (texto "Exportar pagina", sem icone) virou
               o `BotaoExportar` do sistema: mesma acao, mesma aparencia e mesmo
               icone de planilha das outras listas. A montagem das linhas continua
               igual - Device ID primeiro, depois as colunas na ordem da tabela. */
            <BotaoExportar
              className="ml-auto"
              rotulo={`Excel (${linhas.length})`}
              nomeArquivo="BUSCA_DISPOSITIVOS"
              linhas={() =>
                linhas.map((l) => {
                  const r: Record<string, any> = { 'Device ID': l.deviceId ?? '' };
                  COLUNAS.forEach((c) => {
                    r[c.rotulo] = (l as any)[c.campo] ?? '';
                  });
                  return r;
                })
              }
            />
          )}
        </div>

        {/* Sem busca nao ha requisicao - a tela nao varre o parque ao abrir. */}
        {!buscou ? (
          <div className="rounded-lg border border-line bg-surface p-10 text-center shadow-card">
            <p className="text-[12.5px] text-muted-foreground">
              Escolha o tipo de busca, informe o valor e clique em <strong>Pesquisar</strong>.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
            <div className="overflow-x-auto">
              <div style={{ minWidth: 56 + 200 + COLUNAS.length * 130 }}>
                <div
                  className={CABECALHO_TABELA_COMPACTO}
                  style={{ gridTemplateColumns: grade }}
                >
                  <div className="text-center">Opções</div>
                  <div>Device ID</div>
                  {COLUNAS.map((c) => (
                    <div key={String(c.campo)} className="min-w-0 pr-2">
                      <span className="block truncate">{c.rotulo}</span>
                    </div>
                  ))}
                </div>

                <div className="max-h-[58vh] overflow-y-auto">
                  {carregando && (
                    <div className="p-8 text-center text-[12.5px] text-muted-foreground">Pesquisando…</div>
                  )}

                  {!carregando && erro && <div className="p-8 text-center text-[12.5px] text-danger">{erro}</div>}

                  {!carregando && !erro && !linhas.length && (
                    <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                      Nenhum dispositivo encontrado com esse valor.
                    </div>
                  )}

                  {!carregando &&
                    !erro &&
                    linhas.map((d, i) => (
                      <div
                        key={d.deviceId || `sem-device-${i}`}
                        className="grid items-center border-b px-3 py-1.5 hover:bg-surface-2"
                        style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)' }}
                      >
                        <div className="flex justify-center">
                          <MenuAcoes<Dispositivo>
                            linha={d}
                            aberto={menuAberto === d.deviceId}
                            onAlternar={() => setMenuAberto(menuAberto === d.deviceId ? null : d.deviceId ?? null)}
                            onFechar={() => setMenuAberto(null)}
                            acoes={[
                              { rotulo: 'Ver informações', ao: (x) => setDetalhe(x) },
                              {
                                rotulo: 'Abrir detalhe completo',
                                /* SO EM APARELHO ATIVO (10/09/2026, a pedido).

                                   A tela de detalhe e feita para operar: ela
                                   sincroniza, toca, reinicia, recadastra. Num
                                   aparelho deletado nada disso existe mais do
                                   lado do Google, entao a pagina abre cheia de
                                   botao que so devolve erro. "Ver informacoes",
                                   que e leitura do que ficou registrado aqui,
                                   continua disponivel para os dois casos - e
                                   justamente o que se quer olhar num aparelho
                                   que saiu da base. */
                                visivel: Boolean(d.deviceId) && !d.deletedAt,
                                ao: (x) =>
                                  x.deviceId &&
                                  window.open(
                                    `/dashboard/dispositivos-novo/${x.grupoCodigo ?? -1}/item/${encodeURIComponent(
                                      x.deviceId
                                    )}`,
                                    '_blank'
                                  ),
                              },
                            ]}
                          />
                        </div>

                        <div className="min-w-0 pr-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Num className="block truncate text-[11px] font-semibold">
                              {texto(d.deviceId)}
                            </Num>
                          </div>
                        </div>

                        {COLUNAS.map((c) => {
                          const bruto = (d as any)[c.campo];
                          let valorCelula = texto(bruto);
                          let classe = 'text-muted-foreground';

                          if (c.campo === 'device_log_data') valorCelula = fDateSimple(d.device_log_data) || '—';
                          if (c.campo === 'deletedAt') {
                            valorCelula = d.deletedAt ? fDateSimple(d.deletedAt) || 'sim' : '—';
                            if (d.deletedAt) classe = 'font-bold text-danger';
                          }
                          if (c.campo === 'bateria' && bruto !== null && bruto !== undefined && bruto !== '') {
                            valorCelula = `${bruto}%`;
                            if (Number(bruto) <= 20) classe = 'font-bold text-danger';
                          }

                          return (
                            <div key={String(c.campo)} className="min-w-0 pr-2">
                              {c.mono ? (
                                <Num className={`block truncate text-[11px] ${classe}`}>{valorCelula}</Num>
                              ) : (
                                <span className={`block truncate text-[12px] ${classe}`} title={valorCelula}>
                                  {valorCelula}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5 pr-16 text-[12px] text-muted-foreground">
              <span className="flex items-center gap-2">
                <span>
                  Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num>
                </span>
                <label className="flex items-center gap-1.5">
                  <span className="text-faint">por página</span>
                  <select
                    value={linhasPorPagina}
                    onChange={(e) => {
                      setPagina(0);
                      setLinhasPorPagina(Number(e.target.value));
                    }}
                    className="h-7 rounded-md border border-line bg-surface px-1.5 text-[12px] tabular-nums outline-none focus:border-primary"
                  >
                    {OPCOES_LINHAS.map((n) => (
                      <option key={n} value={n}>
                        {rotuloLinhas(n)}
                      </option>
                    ))}
                  </select>
                </label>
              </span>

              <span className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={pagina === 0}
                  onClick={() => setPagina((p) => Math.max(0, p - 1))}
                  className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                >
                  Anterior
                </button>
                <span>
                  <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
                </span>
                <button
                  type="button"
                  disabled={pagina + 1 >= totalPaginas}
                  onClick={() => setPagina((p) => p + 1)}
                  className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
                >
                  Próxima
                </button>
              </span>
            </div>
          </div>
        )}
      </div>

      <Modal
        aberto={modalImei}
        titulo="Registrar IMEIs"
        descricao="Os aparelhos passam a ser aceitos no provisionamento."
        icone="+"
        largura="max-w-[520px]"
        onFechar={() => setModalImei(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setModalImei(false)} disabled={enviandoImeis}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={registrarImeis}
              disabled={enviandoImeis || !listaImeis.length}
              className="h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground disabled:opacity-40"
            >
              {enviandoImeis ? 'Enviando...' : `Registrar (${listaImeis.length})`}
            </button>
          </>
        }
      >
        <Campo rotulo="IMEIs" obrigatorio dica="Um por linha, ou separados por vírgula, ponto-e-vírgula ou espaço.">
          <textarea
            value={imeis}
            onChange={(e) => setImeis(e.target.value)}
            rows={6}
            placeholder={'351234567890123\n351234567890124'}
            className="w-full rounded-lg border border-line bg-surface p-3 tabular-nums text-[12px] text-ink outline-none placeholder:font-sans placeholder:text-faint focus:border-primary"
          />
        </Campo>

        {/* Contagem visivel: colar uma lista grande e nao saber quantos itens
            foram reconhecidos e onde se manda 40 achando que sao 50. */}
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
          <Num className="font-bold text-ink">{listaImeis.length}</Num> IMEI(s) reconhecido(s) no texto.
        </div>
      </Modal>

      {detalhe?.deviceId && (
        <ModalDispositivoInfo
          open={Boolean(detalhe)}
          setOpen={(v: boolean) => !v && setDetalhe(null)}
          device={detalhe as any}
        />
      )}
    </Page>
  );
}
